import React, { useState } from 'react';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInvoice } from '../context/InvoiceContext';
import ProductSelector from './ProductSelector';
import CustomerSelector from './CustomerSelector';
import 'react-datepicker/dist/react-datepicker.css';

const { FiPlus, FiTrash2, FiSave, FiDollarSign, FiPercent, FiHash, FiRepeat } = FiIcons;

const InvoiceForm = () => {
  const { addInvoice, settings, saveProduct, saveCustomer, getInvoiceNumberInfo } = useInvoice();
  const defaultNumberInfo = getInvoiceNumberInfo('default');
  
  const [formData, setFormData] = useState({
    customer: {
      name: '',
      address: '',
      phone: '',
      email: ''
    },
    items: [
      {
        name: '',
        description: '',
        quantity: 1,
        price: 0,
        total: 0
      }
    ],
    paymentDate: new Date(),
    paymentEndDate: null,
    isDateRange: false,
    isExportation: true,
    currency: 'USD',
    includeTax: false,
    taxRate: 10,
    processingFee: 0,
    notes: '',
    cnae: '7319-0/03',
    showEmail: false,
    invoiceNumber: defaultNumberInfo.number,
    invoiceType: 'default',
    isRecurring: false,
    recurringInterval: 'month',
  });

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' }
  ];

  const cnaeOptions = [
    { code: '7911-2/00', description: 'Agências de viagens' },
    { code: '7319-0/03', description: 'Marketing direto' }
  ];

  const getCurrencySymbol = (code) => {
    return currencies.find(c => c.code === code)?.symbol || '$';
  };

  const handleCustomerChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      customer: { ...prev.customer, [field]: value }
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'price') {
      newItems[index].total = newItems[index].quantity * newItems[index].price;
    }
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          name: '',
          description: '',
          quantity: 1,
          price: 0,
          total: 0
        }
      ]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateTax = (subtotal) => {
    return formData.includeTax ? subtotal * (formData.taxRate / 100) : 0;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax(subtotal);
    return subtotal + tax + formData.processingFee;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    saveCustomer(formData.customer);
    formData.items.forEach(item => {
      if (item.name && item.description) {
        saveProduct(item);
      }
    });

    const subtotal = calculateSubtotal();
    const tax = calculateTax(subtotal);
    const total = calculateTotal();

    const invoice = {
      ...formData,
      subtotal,
      tax,
      total,
      companyInfo: { ...settings.companyInfo, logoUrl: settings.logoUrl },
      colors: settings.colors
    };

    addInvoice(invoice);

    const newNumberInfo = getInvoiceNumberInfo('default');
    setFormData({
      customer: { name: '', address: '', phone: '', email: '' },
      items: [{ name: '', description: '', quantity: 1, price: 0, total: 0 }],
      paymentDate: new Date(),
      paymentEndDate: null,
      isDateRange: false,
      isExportation: true,
      currency: 'USD',
      includeTax: false,
      taxRate: 10,
      processingFee: 0,
      notes: '',
      cnae: '7319-0/03',
      showEmail: false,
      invoiceNumber: newNumberInfo.number,
      invoiceType: 'default',
      isRecurring: false,
      recurringInterval: 'month',
    });

    alert('Invoice created successfully!');
  };

  const subtotal = calculateSubtotal();
  const tax = calculateTax(subtotal);
  const total = calculateTotal();

  return (
    <div className="max-w-4xl mx-auto p-6">
      <motion.form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-lg overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="p-8">
          {/* Invoice Number */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Invoice Number</h3>
            <div className="flex items-center">
              <div className="relative w-full max-w-xs">
                <SafeIcon icon={FiHash} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <div className="flex">
                  <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-700 rounded-l-lg font-mono font-semibold">
                    INV-
                  </span>
                  <input
                    type="text"
                    value={formData.invoiceNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Invoice Number"
                  />
                </div>
              </div>
              <p className="ml-4 text-sm text-gray-500">
                Default next: <span className="font-mono font-semibold">INV-{settings.invoiceNumber}</span>
              </p>
            </div>
          </div>

          {/* Customer Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Customer Information</h3>
            <CustomerSelector
              customer={formData.customer}
              onChange={handleCustomerChange}
            />
          </div>

          {/* Currency Selection */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Currency Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {currencies.map((currency) => (
                <motion.label
                  key={currency.code}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.currency === currency.code
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <input
                    type="radio"
                    name="currency"
                    value={currency.code}
                    checked={formData.currency === currency.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                    className="sr-only"
                  />
                  <div className="flex items-center">
                    <span className="text-2xl font-bold mr-3">{currency.symbol}</span>
                    <div>
                      <div className="font-medium text-gray-900">{currency.code}</div>
                      <div className="text-sm text-gray-500">{currency.name}</div>
                    </div>
                  </div>
                </motion.label>
              ))}
            </div>
          </div>

          {/* Invoice Items */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Invoice Items</h3>
              <motion.button
                type="button"
                onClick={addItem}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                Add Item
              </motion.button>
            </div>
            <div className="space-y-4">
              {formData.items.map((item, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-50 p-4 rounded-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900">Item {index + 1}</h4>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <ProductSelector
                    item={item}
                    onChange={(field, value) => handleItemChange(index, field, value)}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value) || 1)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price ({getCurrencySymbol(formData.currency)})
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', Number(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total
                      </label>
                      <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 font-medium">
                        {getCurrencySymbol(formData.currency)}{item.total.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Recurring Payment */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiRepeat} className="w-5 h-5 mr-3 text-gray-400" />
              Recurring Payment
            </h3>
            <div className="bg-gray-50 p-6 rounded-lg space-y-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isRecurring}
                  onChange={(e) => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  This is a recurring invoice (creates a subscription)
                </span>
              </label>
              {formData.isRecurring && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginTop: '1rem' }}
                  className="pl-7"
                >
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Interval
                  </label>
                  <select
                    value={formData.recurringInterval}
                    onChange={(e) => setFormData(prev => ({ ...prev, recurringInterval: e.target.value }))}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                    <option value="week">Weekly</option>
                    <option value="day">Daily</option>
                  </select>
                </motion.div>
              )}
            </div>
          </div>

          {/* Tax and Fee Settings */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Tax & Fees</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.includeTax}
                    onChange={(e) => setFormData(prev => ({ ...prev, includeTax: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700"> Include Tax </span>
                </label>
              </div>
              {formData.includeTax && (
                <motion.div
                  className="flex items-center space-x-4"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <label className="flex items-center space-x-2">
                    <SafeIcon icon={FiPercent} className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Tax Rate:</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.taxRate}
                    onChange={(e) => setFormData(prev => ({ ...prev, taxRate: Number(e.target.value) || 0 }))}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </motion.div>
              )}
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Processing Fee:</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.processingFee}
                  onChange={(e) => setFormData(prev => ({ ...prev, processingFee: Number(e.target.value) || 0 }))}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-500">{getCurrencySymbol(formData.currency)}</span>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Payment Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    checked={formData.isDateRange}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      isDateRange: e.target.checked,
                      paymentEndDate: e.target.checked ? new Date() : null
                    }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700"> Payment Date Range </span>
                </label>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.isDateRange ? 'Payment Start Date' : 'Payment Date'}
                    </label>
                    <DatePicker
                      selected={formData.paymentDate}
                      onChange={(date) => setFormData(prev => ({ ...prev, paymentDate: date }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      dateFormat="yyyy-MM-dd"
                    />
                  </div>
                  {formData.isDateRange && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment End Date
                      </label>
                      <DatePicker
                        selected={formData.paymentEndDate}
                        onChange={(date) => setFormData(prev => ({ ...prev, paymentEndDate: date }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        dateFormat="yyyy-MM-dd"
                        minDate={formData.paymentDate}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    checked={formData.isExportation}
                    onChange={(e) => setFormData(prev => ({ ...prev, isExportation: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700"> Exportation Sale </span>
                </label>
                
                {/* CNAE Field */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CNAE (Código Nacional de Atividade Econômica)
                  </label>
                  <select
                    value={formData.cnae}
                    onChange={(e) => setFormData(prev => ({ ...prev, cnae: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {cnaeOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.code} - {option.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Show Email Option */}
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.showEmail}
                      onChange={(e) => setFormData(prev => ({ ...prev, showEmail: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700"> Show Email in Invoice </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Summary */}
          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Invoice Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">{getCurrencySymbol(formData.currency)}{subtotal.toFixed(2)}</span>
              </div>
              {formData.includeTax && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax ({formData.taxRate}%):</span>
                  <span className="font-medium">{getCurrencySymbol(formData.currency)}{tax.toFixed(2)}</span>
                </div>
              )}
              {formData.processingFee > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Processing Fee:</span>
                  <span className="font-medium">{getCurrencySymbol(formData.currency)}{formData.processingFee.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t pt-2">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-blue-600">{getCurrencySymbol(formData.currency)}{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
            Create Invoice
          </motion.button>
        </div>
      </motion.form>
    </div>
  );
};

export default InvoiceForm;